import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorAutomatic - The automatic-discount conflict error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorAutomatic',
    'cartDiscountErrorShipping',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Applies a discount code from the text input form.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    await this.#applyDiscountCode(discountCode.value, {
      clearInput: discountCode,
      event,
    });
  };

  /**
   * Applies a discount code from an available-offer chip.
   * @param {MouseEvent} event
   */
  applyOffer = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.cart-discount__offer');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const code = button.dataset.discountCode;
    if (!code) return;

    await this.#applyDiscountCode(code, { event });
  };

  /**
   * Shared cart discount apply flow.
   * @param {string} discountCodeValue
   * @param {{ clearInput?: HTMLInputElement; event?: Event }} [options]
   */
  #applyDiscountCode = async (discountCodeValue, options = {}) => {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorAutomatic,
      cartDiscountErrorShipping,
    } = this.refs;
    const { clearInput, event } = options;

    if (!discountCodeValue || typeof this.dataset.sectionId !== 'string') return;

    if (this.dataset.hasAutomaticDiscount === 'true') {
      cartDiscountError.classList.remove('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      cartDiscountErrorAutomatic.classList.remove('hidden');
      return;
    }

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some((code) => code.toLowerCase() === discountCodeValue.toLowerCase())) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorAutomatic.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === false;
        })
      ) {
        if (clearInput) clearInput.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          if (clearInput) clearInput.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      if (this.dataset.reloadAfter === 'true') {
        window.location.reload();
        return;
      }
      await morphSection(this.dataset.sectionId, newHtml, this.#morphMode, { injectStylesheet: true });
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      if (event) {
        cartPerformance.measureFromEvent('discount-update:user-action', event);
      }
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      (!(event instanceof MouseEvent) && !(event instanceof KeyboardEvent)) ||
      !(event.target instanceof Element) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const remainingDiscounts = existingDiscounts.filter(
      (code) => code.toLowerCase() !== discountCode.toLowerCase()
    );

    const abortController = this.#createAbortController();

    try {
      const cartUrl = window.Theme?.routes?.cart_update_url || Theme.routes?.cart_update_url || '/cart/update.js';
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: remainingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(cartUrl, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      if (this.dataset.reloadAfter === 'true') {
        window.location.reload();
        return;
      }
      if (data.sections && data.sections[this.dataset.sectionId]) {
        await morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], this.#morphMode, {
          injectStylesheet: true,
        });
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to remove discount:', error);
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'automatic'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorAutomatic,
      cartDiscountErrorShipping,
    } = this.refs;
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorAutomatic.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    cartDiscountError.classList.remove('hidden');
    if (type === 'shipping') {
      cartDiscountErrorShipping.classList.remove('hidden');
    } else if (type === 'automatic') {
      cartDiscountErrorAutomatic.classList.remove('hidden');
    } else {
      cartDiscountErrorDiscountCode.classList.remove('hidden');
    }
  }

  /**
   * Inside the cart drawer, a full header morph knocks the open modal dialog out of
   * the top layer, so only the keyed drawer contents are morphed.
   * @returns {'hydration' | 'full'}
   */
  get #morphMode() {
    return this.dataset.drawer !== undefined ? 'hydration' : 'full';
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
